import { useCallback, useEffect, useState, type RefObject } from 'react';

export interface PresentationFullscreenState {
  supported: boolean;
  active: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
}

/**
 * SPEC-011 PRES-REQ-008/FM-003/plan §16 — never a hard failure: unsupported
 * or denied Fullscreen API simply keeps `active: false`, and presentation
 * continues to work in the normal browser viewport (AC-027).
 */
export function usePresentationFullscreen(
  targetRef: RefObject<HTMLElement | null>,
): PresentationFullscreenState {
  const supported = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onChange = () => setActive(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const enter = useCallback(async () => {
    if (!supported || !targetRef.current) return;
    try {
      await targetRef.current.requestFullscreen();
    } catch {
      // Denied or unavailable — continue in viewport (FM-003).
    }
  }, [supported, targetRef]);

  const exit = useCallback(async () => {
    if (!document.fullscreenElement) return;
    try {
      await document.exitFullscreen();
    } catch {
      // Nothing else to do; state stays consistent via fullscreenchange.
    }
  }, []);

  const toggle = useCallback(async () => {
    if (document.fullscreenElement) await exit();
    else await enter();
  }, [enter, exit]);

  return { supported, active, enter, exit, toggle };
}
